import React, { Dispatch, useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { Icon } from "@cadmakers-ui/icons";
import { ApiRequest, IAppState, LoadingState, useShowSnack } from "../../Common";
import { endpoints as topicEndpoints } from "../../Topic/Constants/endpoints";
import { Application } from "../../App";
import { ResponseResult } from "../../Common/Models/ResponseResult";
import ResponseHandler from "../../App/ResponseHandler";
import { Company } from "../../Company/Models/Company";
import { Topic } from "../../Topic/Models/Topic";
import OverviewSideBar from "./OverviewSideBar";
import { FeedbackCategory } from "../../Feedback/Constants/feedbackEnums";
import FeedbackListContainer from "../../Feedback/Components/Common/FeedbackList/FeedbackListContainer";
import { SearchContextType } from "../../Layout/Constants/LayoutEnums";
import styles from "../Styles/OverviewFeedbackListContainer.module.scss";

type TParams = {
  id: string;
};

type StateProps = {
  company?: Company;
  applications: Application[];
  appsLoadingState: LoadingState;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  company: state.app.company,
  applications: state.app.applications,
  appsLoadingState: state.app.appsLoadingState
});

const OverviewFeedbackListContainer: React.FC<RouteComponentProps<TParams> &
  StateProps> = props => {
  const feedbackCategories = [
    { label: "Feature Request", value: FeedbackCategory.FeatureRequest },
    { label: "Bug Report", value: FeedbackCategory.BugReport },
    { label: "Resource Request", value: FeedbackCategory.ResourceRequest }
  ];

  const [topics, setTopics] = useState([] as Topic[]);
  const [topicsAreLoaded, setTopicsAreLoaded] = useState(false);
  const [searchbarQuery, setSearchbarQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([] as any[]);
  const [selectedAppIds, setSelectedAppIds] = useState([] as number[]);
  const [selectedTopicIds, setSelectedTopicIds] = useState([] as number[]);
  const [sideBarIsOpen, setSideBarIsOpen] = useState(true);
  const showSnack = useShowSnack();

  const getTopics = () => {
    ApiRequest.fromPath(
      topicEndpoints.topics.getByCompany(props.company ? props.company.shortName : "")
    )
      .withAuthHeader()
      .get()
      .then(response => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult) => {
            setTopics(result.payload);
            setTopicsAreLoaded(true);
          },
          result => {
            const label = `Error retrieving feedbacks: "${result.error.message}"`;
            showSnack("RETRIEVE_TOPICS", {
              label,
              timeout: 2000
            });
          },
          () => {
            console.error("Unknown error retrieving feedbacks");
          }
        ).catch(() => {});
      });
  };

  const onChangeSearch = (query: string) => {
    setSearchbarQuery(query);
  };

  const filteredApplications = props.applications.filter(application =>
    application.name.toLowerCase().startsWith(searchbarQuery.toLowerCase())
  );
  const filteredTopics = topics.filter(topic =>
    topic.name.toLowerCase().startsWith(searchbarQuery.toLowerCase())
  );

  if (!topicsAreLoaded) {
    getTopics();
  }

  const onSubmit = (categories: any[], applicationIds: number[], topicIds: number[]) => {
    setSelectedCategories(categories.map(category => category.value));
    setSelectedAppIds(applicationIds);
    setSelectedTopicIds(topicIds);
  };

  return (
    <div className={styles.container}>
      {props.appsLoadingState.isLoaded && topicsAreLoaded && (
        <OverviewSideBar
          isOpen={sideBarIsOpen}
          categories={feedbackCategories}
          applications={filteredApplications}
          topics={filteredTopics}
          onChangeSearch={onChangeSearch}
          onSubmit={onSubmit}
        />
      )}
      <div
        className={styles.toggle}
        onClick={e => {
          setSideBarIsOpen(!sideBarIsOpen);
        }}
      >
        <Icon className={styles.icon} appName="GeneralLayout" assetName="GENERAL_L_Menu" />
      </div>
      <div className={styles.feedbacklist}>
        <FeedbackListContainer
          listId={`OVERVIEW_FEEDBACK_LIST_${props.match.params.id}`}
          datagridHeight="100%"
          paging
          enableSearch
          context={{ type: SearchContextType.All, itemId: props.match.params.id }}
          selectedCategories={selectedCategories}
          selectedApplications={selectedAppIds}
          selectedTopics={selectedTopicIds}
        />
      </div>
    </div>
  );
};

export default connect(mapStateToProps)(OverviewFeedbackListContainer);
